import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  GlobeAltIcon,
  EnvelopeIcon,
  MapPinIcon,
  DocumentTextIcon,
  PhoneIcon,
  HeartIcon,
  MapIcon,
  Squares2X2Icon
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartSolidIcon } from '@heroicons/react/24/solid';
import { suppliersAPI, usersAPI } from '../services/api.ts';
import api from '../services/api.ts';
import { useAuth } from '../contexts/AuthContext.tsx';
import locationService from '../services/locationService.ts';

// Google Maps types
declare global {
  interface Window {
    google: any;
  }
}

// Type aliases for Google Maps
type GoogleMap = any;
type GoogleMarker = any;
type GoogleInfoWindow = any;
type GoogleLatLngBounds = any;
type GoogleSize = any;
type GooglePoint = any;
type GoogleLatLngLiteral = any;

interface Supplier {
  id: string;
  name: string;
  contact_email?: string;
  contact_phone?: string;
  address?: string;
  latitude?: string | number;
  longitude?: string | number;
  postcode?: string;
  distance_km?: number;
  distance_display?: string;
  is_active: boolean;
  created_by?: string;
  customer_note?: string;
  note_created_at?: string;
  note_author_name?: string;
  has_customer_note: boolean;
  is_favorite: boolean;
  customer_contact_name?: string;
  customer_contact_email?: string;
  customer_phone?: string;
  preferred_order_method?: 'phone' | 'email' | 'purchase_order' | 'zahara';
  availability_check_method?: 'phone' | 'email' | 'website';
  created_at: string;
  updated_at: string;
}

interface ZaharaMatch {
  id: string;
  supplier_id: string;
  zahara_supplier_id: number;
  supplier_name: string;
}

const Suppliers: React.FC = () => {
  const { user, customer } = useAuth();
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [mapSuppliers, setMapSuppliers] = useState<Supplier[]>([]);
  const [zaharaMatches, setZaharaMatches] = useState<ZaharaMatch[]>([]);
  const [zaharaConfigured, setZaharaConfigured] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showFavorites, setShowFavorites] = useState(true);
  const [showNearMe, setShowNearMe] = useState(false);
  const [userLocation, setUserLocation] = useState<{latitude: number, longitude: number} | null>(null);
  const [locationLoading, setLocationLoading] = useState(false);
  const [showMapView, setShowMapView] = useState(false);
  const [mapLoaded, setMapLoaded] = useState(false);
  const mapRef = useRef<HTMLDivElement>(null);
  const googleMapRef = useRef<GoogleMap | null>(null);
  const markersRef = useRef<GoogleMarker[]>([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState<Supplier | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  const [formData, setFormData] = useState({
    name: '',
    contact_email: '',
    contact_phone: '',
    address: ''
  });

  const [customerDetailsData, setCustomerDetailsData] = useState({
    contact_name: '',
    contact_email: '',
    phone: '',
    preferred_order_method: '' as 'phone' | 'email' | 'purchase_order' | 'zahara' | '',
    availability_check_method: '' as 'phone' | 'email' | 'website' | ''
  });

  const [customerNote, setCustomerNote] = useState('');
  const [timelineEvents, setTimelineEvents] = useState<any[]>([]);

  const isAdmin = user?.role === 'admin';

  const hasZaharaMatch = (supplierId: string) => {
    const hasMatch = zaharaMatches.some(match => match.supplier_id === supplierId);
    console.log(`Checking match for supplier ${supplierId}:`, hasMatch, 'Total matches:', zaharaMatches.length);
    return hasMatch;
  };

  const fetchSuppliers = useCallback(async (search = '', page = currentPage, showLoading = true) => {
    try {
      if (showLoading) setLoading(true);
      const params = {
        page: page,
        limit: 12,
        search: search,
        nearMe: showNearMe.toString()
      };
      
      const response = showFavorites 
        ? await suppliersAPI.getFavorites(params)
        : await suppliersAPI.getAll(params);
        
      console.log('Suppliers API response:', response.data);
      console.log('First supplier with customer details:', response.data.suppliers[0]);
      
      setSuppliers(response.data.suppliers);
      setTotalPages(response.data.pagination.pages);
      
      // Update user location if provided
      if (response.data.userLocation) {
        setUserLocation(response.data.userLocation);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load suppliers');
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [currentPage, showFavorites, showNearMe]);

  const fetchMapData = useCallback(async () => {
    try {
      console.log('🗺️ Fetching map data...');
      const response = await suppliersAPI.getMapData(showNearMe);
      
      console.log('🗺️ Map data received:', {
        suppliersCount: response.data.suppliers.length,
        userLocation: response.data.userLocation
      });
      
      setMapSuppliers(response.data.suppliers || []);
      
      if (response.data.userLocation) {
        setUserLocation(response.data.userLocation);
      }
    } catch (err: any) {
      console.error('Error fetching map data:', err);
      setError(err.response?.data?.error || 'Failed to fetch map data');
    }
  }, [showNearMe]);

  const fetchZaharaMatches = async () => {
    try {
      console.log('Fetching Zahara matches...');
      const response = await api.get('/zahara/supplier-matches');
      console.log('Zahara matches response:', response.data);
      if (response.data.success) {
        setZaharaMatches(response.data.data);
        console.log('Set Zahara matches:', response.data.data);
      }
    } catch (err: any) {
      console.log('Zahara matches error:', err.response?.data?.error || err.message);
    }
  };

  // Location detection functions
  const detectLocation = async () => {
    try {
      setLocationLoading(true);
      const location = await locationService.getCurrentLocation();
      
      // Save location to user profile
      await usersAPI.updateLocationSettings({
        latitude: location.latitude,
        longitude: location.longitude,
        postcode: location.postcode
      });
      
      setUserLocation({
        latitude: location.latitude,
        longitude: location.longitude
      });
      
      setMessage({ type: 'success', text: 'Location detected successfully!' });
      
      // Refresh suppliers with new location
      fetchSuppliers();
    } catch (error: any) {
      console.error('Location detection error:', error);
      setMessage({ 
        type: 'error', 
        text: error.message || 'Failed to detect location. Please try again.' 
      });
    } finally {
      setLocationLoading(false);
    }
  };

  const handleNearMeToggle = async () => {
    if (!showNearMe && !userLocation) {
      // First time enabling - try to detect location
      await detectLocation();
    }
    setShowNearMe(!showNearMe);
  };

  const checkZaharaConfiguration = async () => {
    try {
      console.log('Checking Zahara configuration...');
      const response = await api.get('/zahara/settings');
      console.log('Zahara settings response:', response.data);
      
      // The settings endpoint returns data directly, not wrapped in success/data
      const settings = response.data;
      console.log('Settings data:', settings);
      const isConfigured = settings.api_key && settings.nominal_code_id && settings.tax_code_id;
      setZaharaConfigured(isConfigured);
      console.log('Zahara configured:', isConfigured, 'API key:', !!settings.api_key, 'Nominal ID:', !!settings.nominal_code_id, 'Tax ID:', !!settings.tax_code_id);
    } catch (err: any) {
      console.log('Zahara configuration check failed:', err.response?.data?.error || err.message);
      setZaharaConfigured(false);
    }
  };

  const exportToZahara = async (supplierId: string) => {
    try {
      console.log('Exporting supplier to Zahara:', supplierId);
      const response = await api.post('/zahara/export-supplier', { supplier_id: supplierId });
      
      if (response.data.success) {
        console.log('Export successful:', response.data);
        // Refresh matches to show the new Zahara logo
        await fetchZaharaMatches();
        setMessage({ type: 'success', text: `${response.data.supplier_name} exported to Zahara successfully!` });
        // Close the modal after successful export
        setShowAddModal(false);
      }
    } catch (err: any) {
      console.error('Export failed:', err.response?.data?.error || err.message);
      setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to export supplier to Zahara' });
    }
  };

  // Create a ref to store the debounced function
  const debouncedSearchRef = useRef<number | null>(null);

  // Handle search with debouncing - completely separate from pagination
  const handleSearchChange = () => {
    const value = searchInputRef.current?.value || '';
    
    // Clear existing timeout
    if (debouncedSearchRef.current) {
      clearTimeout(debouncedSearchRef.current);
    }
    
    // Set new timeout - always search from page 1, no loading spinner
    debouncedSearchRef.current = window.setTimeout(() => {
      fetchSuppliers(value, 1, false);
    }, 300);
  };

  // Only fetch on page changes
  useEffect(() => {
    fetchSuppliers('', currentPage);
    fetchZaharaMatches();
    checkZaharaConfiguration();
    
    // Load user location on mount
    const loadUserLocation = async () => {
      try {
        const response = await usersAPI.getLocationSettings();
        if (response.data.latitude && response.data.longitude) {
          setUserLocation({
            latitude: response.data.latitude,
            longitude: response.data.longitude
          });
        }
      } catch (error) {
        console.log('No user location found');
      }
    };
    
    loadUserLocation();
  }, [currentPage, showFavorites, fetchSuppliers]);

  // Load Google Maps API
  useEffect(() => {
    const loadGoogleMaps = () => {
      if (window.google && window.google.maps) {
        setMapLoaded(true);
        return;
      }

      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyBShSyYr2n-mrs2b1J23rt-93s3FNgVlOs&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = () => setMapLoaded(true);
      document.head.appendChild(script);
    };

    if (showMapView) {
      loadGoogleMaps();
    }
  }, [showMapView]);

  // Fetch map data when map view is enabled
  useEffect(() => {
    if (showMapView) {
      fetchMapData();
    }
  }, [showMapView, showNearMe, fetchMapData]);

  // Initialize map when Google Maps loads and map data is available
  useEffect(() => {
    console.log('🗺️ Map initialization check:', {
      mapLoaded,
      showMapView,
      mapRefExists: !!mapRef.current,
      mapSuppliersCount: mapSuppliers.length,
      suppliersWithCoords: mapSuppliers.filter(s => s.latitude && s.longitude).length
    });
    
    if (mapLoaded && showMapView && mapRef.current && mapSuppliers.length > 0) {
      // Only initialize if we have suppliers with coordinates
      const suppliersWithCoords = mapSuppliers.filter(s => s.latitude && s.longitude);
      if (suppliersWithCoords.length > 0 || userLocation) {
        console.log('🗺️ Initializing map...');
        initializeMap();
      } else {
        console.log('⚠️ No suppliers with coordinates and no user location - skipping map initialization');
      }
    }
  }, [mapLoaded, showMapView, mapSuppliers, userLocation]);

  const initializeMap = () => {
    if (!mapRef.current || !window.google) return;

    console.log('🗺️ Initializing map with:', {
      mapSuppliersCount: mapSuppliers.length,
      suppliersWithCoords: mapSuppliers.filter(s => s.latitude && s.longitude).length,
      userLocation,
      firstSupplier: mapSuppliers[0]
    });

    // Clear existing markers
    markersRef.current.forEach(marker => marker.setMap(null));
    markersRef.current = [];

    // Determine center point
    let center: GoogleLatLngLiteral;
    if (userLocation) {
      center = { lat: userLocation.latitude, lng: userLocation.longitude };
      console.log('📍 Centering on user location:', center);
    } else {
      // Fallback to first supplier with coordinates
      const firstSupplier = mapSuppliers.find(s => s.latitude && s.longitude);
      if (firstSupplier) {
        center = { lat: parseFloat(String(firstSupplier.latitude!)), lng: parseFloat(String(firstSupplier.longitude!)) };
        console.log('📍 Centering on first supplier:', center);
      } else {
        center = { lat: 51.5074, lng: -0.1278 }; // London fallback
        console.log('📍 Using London fallback:', center);
      }
    }

    // Initialize map
    googleMapRef.current = new window.google.maps.Map(mapRef.current, {
      zoom: 125,
      center: center,
      mapTypeId: window.google.maps.MapTypeId.ROADMAP,
      styles: [
        {
          featureType: 'poi',
          elementType: 'labels',
          stylers: [{ visibility: 'off' }]
        }
      ]
    });

    // Add user location marker if available
    if (userLocation) {
      const userMarker = new window.google.maps.Marker({
        position: { lat: userLocation.latitude, lng: userLocation.longitude },
        map: googleMapRef.current,
        title: 'Your Location',
        icon: {
          url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="8" fill="#3B82F6" stroke="white" stroke-width="2"/>
              <circle cx="12" cy="12" r="3" fill="white"/>
            </svg>
          `),
          scaledSize: new window.google.maps.Size(24, 24),
          anchor: new window.google.maps.Point(12, 12)
        }
      });
      markersRef.current.push(userMarker);
    }

    // Add supplier markers
    mapSuppliers.forEach(supplier => {
      if (supplier.latitude && supplier.longitude) {
        const lat = parseFloat(String(supplier.latitude));
        const lng = parseFloat(String(supplier.longitude));
        
        console.log('📍 Adding marker for supplier:', supplier.name, 'at:', lat, lng);
        
        const marker = new window.google.maps.Marker({
          position: { lat: lat, lng: lng },
          map: googleMapRef.current,
          title: supplier.name,
          icon: {
            url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill="#EF4444"/>
              </svg>
            `),
            scaledSize: new window.google.maps.Size(24, 24),
            anchor: new window.google.maps.Point(12, 24)
          }
        });

        // Add info window
        const infoWindow = new window.google.maps.InfoWindow({
          content: `
            <div class="p-2">
              <h3 class="font-semibold text-lg">${supplier.name}</h3>
              ${supplier.address ? `<p class="text-sm text-gray-600 mb-2">${supplier.address}</p>` : ''}
              ${supplier.distance_display ? `<p class="text-sm text-blue-600 mb-2">📍 ${supplier.distance_display} away</p>` : ''}
              ${supplier.contact_phone ? `<p class="text-sm">📞 ${supplier.contact_phone}</p>` : ''}
              ${supplier.contact_email ? `<p class="text-sm">✉️ ${supplier.contact_email}</p>` : ''}
            </div>
          `
        });

        marker.addListener('click', () => {
          infoWindow.open(googleMapRef.current, marker);
        });

        markersRef.current.push(marker);
      } else {
        console.log('❌ Supplier has no coordinates:', supplier.name);
      }
    });

    // Fit bounds to show all markers
    if (markersRef.current.length > 0) {
      console.log('🎯 Fitting bounds for', markersRef.current.length, 'markers');
      const bounds = new window.google.maps.LatLngBounds();
      markersRef.current.forEach(marker => {
        const position = marker.getPosition();
        if (position) bounds.extend(position);
      });
      googleMapRef.current.fitBounds(bounds);
    } else {
      console.log('⚠️ No markers to fit bounds to');
    }
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (debouncedSearchRef.current) {
        clearTimeout(debouncedSearchRef.current);
      }
    };
  }, []);



  const handleAddSupplier = () => {
    setEditingSupplier(null);
    setFormData({
      name: '',
      contact_email: '',
      contact_phone: '',
      address: ''
    });
    setCustomerDetailsData({
      contact_name: '',
      contact_email: '',
      phone: '',
      preferred_order_method: '',
      availability_check_method: ''
    });
    setCustomerNote('');
    setValidationErrors([]);
    setShowAddModal(true);
  };

  const handleEditSupplier = (supplier: Supplier) => {
    console.log('Editing supplier:', supplier);
    console.log('Customer note:', supplier.customer_note);
    
    setEditingSupplier(supplier);
    setFormData({
      name: supplier.name,
      contact_email: supplier.contact_email || '',
      contact_phone: supplier.contact_phone || '',
      address: supplier.address || ''
    });
    
    setCustomerDetailsData({
      contact_name: supplier.customer_contact_name || '',
      contact_email: supplier.customer_contact_email || '',
      phone: supplier.customer_phone || '',
      preferred_order_method: supplier.preferred_order_method || '',
      availability_check_method: supplier.availability_check_method || ''
    });
    
    setCustomerNote(supplier.customer_note || '');
    setValidationErrors([]);
    setShowAddModal(true);
    
    // Fetch timeline events for this supplier
    fetchTimelineEvents(supplier.id);
  };

  const handleSubmitSupplier = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setValidationErrors([]);

    try {
      const submitData = {
        name: formData.name.trim(),
        contact_email: formData.contact_email.trim() || undefined,
        contact_phone: formData.contact_phone.trim() || undefined,
        address: formData.address.trim() || undefined,
      };

      if (editingSupplier) {
        // Update existing supplier (only if user is the creator)
        if (editingSupplier.created_by === customer?.id) {
          await suppliersAPI.update(editingSupplier.id, submitData);
        }
        
        // Update customer details
          await suppliersAPI.updateCustomerDetails(editingSupplier.id, {
          contact_name: customerDetailsData.contact_name || undefined,
          contact_email: customerDetailsData.contact_email || undefined,
          phone: customerDetailsData.phone || undefined,
          preferred_order_method: customerDetailsData.preferred_order_method || undefined,
          availability_check_method: customerDetailsData.availability_check_method || undefined
        });
        
        // Update customer note
        if (customerNote.trim()) {
          // Add or update note
          await api.post(`/suppliers/${editingSupplier.id}/notes`, {
            note: customerNote.trim()
          });
        }
      } else {
        // Create new supplier
        const response = await suppliersAPI.create(submitData);
        
        // Add customer details
        await suppliersAPI.updateCustomerDetails(response.data.supplier.id, {
          contact_name: customerDetailsData.contact_name || undefined,
          contact_email: customerDetailsData.contact_email || undefined,
          phone: customerDetailsData.phone || undefined,
          preferred_order_method: customerDetailsData.preferred_order_method || undefined,
          availability_check_method: customerDetailsData.availability_check_method || undefined
        });
        
        // Add note if provided
        if (customerNote.trim()) {
          await api.post(`/suppliers/${response.data.supplier.id}/notes`, {
            note: customerNote.trim()
          });
        }
      }

      setShowAddModal(false);
      fetchSuppliers();
    } catch (err: any) {
      if (err.response?.data?.details) {
        setValidationErrors(err.response.data.details);
      } else {
        setValidationErrors([err.response?.data?.error || 'Failed to save supplier']);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const fetchTimelineEvents = async (supplierId: string) => {
    try {
      const response = await api.get(`/suppliers/${supplierId}/timeline`);
      setTimelineEvents(response.data.events || []);
    } catch (error) {
      console.error('Error fetching timeline events:', error);
      setTimelineEvents([]);
    }
  };

  const handleAddNote = async () => {
    if (!editingSupplier || !customerNote.trim()) return;
    
    try {
      // Add note to database
      await api.post(`/suppliers/${editingSupplier.id}/notes`, {
        note: customerNote.trim()
      });
      
      // Refresh timeline events
      await fetchTimelineEvents(editingSupplier.id);
      
      // Clear the note field
      setCustomerNote('');
      
      // Show success message (optional)
      console.log('Note added successfully!');
    } catch (error: any) {
      console.error('Error adding note:', error);
      // You could add a toast notification here
    }
  };

  const handleDeleteSupplier = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this supplier?')) {
      return;
    }

    try {
      await suppliersAPI.delete(id);
      fetchSuppliers();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete supplier');
    }
  };

  const toggleFavorite = async (supplierId: string, isFavorite: boolean) => {
    try {
      if (isFavorite) {
        await suppliersAPI.removeFavorite(supplierId);
      } else {
        await suppliersAPI.addFavorite(supplierId);
      }
      
      // Update the supplier in the local state
      setSuppliers(prevSuppliers => 
        prevSuppliers.map(supplier => 
          supplier.id === supplierId 
            ? { ...supplier, is_favorite: !isFavorite }
            : supplier
        )
      );
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update favorite');
    }
  };


  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Suppliers</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage your equipment suppliers and customer-specific details
          </p>
        </div>
        {isAdmin && (
          <button
            onClick={handleAddSupplier}
            className="btn btn-primary"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Add Supplier
          </button>
        )}
      </div>

      {/* Search and Filters */}
      <div className="flex gap-4">
        <div className="flex-1 relative">
          <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            ref={searchInputRef}
            type="text"
            placeholder="Search suppliers or town ..."
            onChange={handleSearchChange}
            className="w-full input"
          />
        </div>
        
        <button
          onClick={() => setShowFavorites(!showFavorites)}
          className={`transition-colors ${
            showFavorites
              ? 'btn btn-secondary'
              : 'btn btn-outline'
          }`}
        >
          <HeartIcon className="h-5 w-5 mr-2" />
          {showFavorites ? 'Show All' : 'My Favorites'}
        </button>
        
        <button
          onClick={handleNearMeToggle}
          disabled={locationLoading}
          className={`transition-colors ${
            showNearMe
              ? 'btn btn-primary'
              : 'btn btn-outline'
          } ${locationLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          {locationLoading ? (
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
          ) : (
            <MapIcon className="h-5 w-5 mr-2" />
          )}
          {showNearMe ? 'Show All Locations' : 'Near Me'}
        </button>
        
        <button
          onClick={() => setShowMapView(!showMapView)}
          className={`transition-colors ${
            showMapView
              ? 'btn btn-primary'
              : 'btn btn-outline'
          }`}
        >
          {showMapView ? (
            <Squares2X2Icon className="h-5 w-5 mr-2" />
          ) : (
            <MapIcon className="h-5 w-5 mr-2" />
          )}
          {showMapView ? 'List View' : 'Map View'}
        </button>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-600">{error}</p>
          <button
            onClick={() => setError(null)}
            className="mt-2 text-sm text-red-500 hover:text-red-700"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Success/Error Message Display */}
      {message && (
        <div className={`border rounded-lg p-4 ${
          message.type === 'success' 
            ? 'bg-green-50 border-green-200' 
            : 'bg-red-50 border-red-200'
        }`}>
          <p className={message.type === 'success' ? 'text-green-600' : 'text-red-600'}>
            {message.text}
          </p>
          <button
            onClick={() => setMessage(null)}
            className={`mt-2 text-sm hover:opacity-70 ${
              message.type === 'success' ? 'text-green-500' : 'text-red-500'
            }`}
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Suppliers Grid */}
      {suppliers.length === 0 ? (
        <div className="text-center py-12">
          <GlobeAltIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {showFavorites ? 'No favorites yet' : 'No suppliers found'}
          </h3>
          <p className="text-gray-500 mb-4">
            {showFavorites 
              ? 'Start adding suppliers to your favorites by clicking the heart icon.'
              : searchInputRef.current?.value 
                ? 'Try adjusting your search terms.' 
                : 'Get started by adding your first supplier.'
            }
          </p>
          {isAdmin && !searchInputRef.current?.value && !showFavorites && (
            <button
              onClick={handleAddSupplier}
              className="btn btn-primary"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Add Supplier
            </button>
          )}
        </div>
      ) : (
        <>
          {showMapView ? (
            <div className="w-full h-[600px] rounded-lg border border-gray-200 overflow-hidden">
              <div ref={mapRef} className="w-full h-full" />
              {!mapLoaded && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                    <p className="text-gray-600">Loading map...</p>
                  </div>
                </div>
              )}
              {mapLoaded && mapSuppliers.length > 0 && mapSuppliers.filter(s => s.latitude && s.longitude).length === 0 && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                  <div className="text-center">
                    <MapIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 text-lg">No suppliers with location data</p>
                    <p className="text-gray-500 text-sm">Suppliers need coordinates to appear on the map</p>
                  </div>
                </div>
              )}
            </div>
          ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {suppliers.map((supplier) => {
              console.log('Rendering supplier:', supplier.name, 'hasZaharaMatch:', hasZaharaMatch(supplier.id));
              console.log('Supplier customer details:', {
                customer_contact_name: supplier.customer_contact_name,
                customer_contact_email: supplier.customer_contact_email,
                preferred_order_method: supplier.preferred_order_method,
                availability_check_method: supplier.availability_check_method
              });
              return (
              <div key={supplier.id} className="card hover:shadow-lg transition-shadow">
                <div className="card-body">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      <h3 className="font-semibold text-gray-900 line-clamp-2">{supplier.name}</h3>
                      {supplier.distance_display && (
                        <div className="flex items-center text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                          <MapPinIcon className="h-4 w-4 mr-1" />
                          {supplier.distance_display}
                        </div>
                      )}
                      {hasZaharaMatch(supplier.id) && (
                        <div className="flex items-center">
                          <img
                            src="/images/zahara-logo.svg"
                            alt="Zahara Integration"
                            className="h-4 w-4"
                            title="Matched with Zahara"
                            onError={(e) => {
                              console.log('Logo failed to load:', e);
                              e.currentTarget.style.display = 'none';
                            }}
                            onLoad={() => console.log('Logo loaded successfully')}
                          />
                         
                        </div>
                      )}
                    </div>
                    <div className="flex items-center space-x-2">
                      {isAdmin && (
                        <>
                          <button
                            onClick={() => handleEditSupplier(supplier)}
                            className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                            title={supplier.created_by === customer?.id ? "Edit supplier (full access)" : "Edit supplier (customer details only)"}
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                          {supplier.created_by === customer?.id && (
                          <button
                            onClick={() => handleDeleteSupplier(supplier.id)}
                            className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                            title="Delete supplier"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                          )}
                        </>
                      )}
                      <button
                        onClick={() => toggleFavorite(supplier.id, supplier.is_favorite)}
                        className="flex-shrink-0"
                        title={supplier.is_favorite ? "Remove from favorites" : "Add to favorites"}
                      >
                        {supplier.is_favorite ? (
                          <HeartSolidIcon className="h-5 w-5 text-red-500" />
                        ) : (
                          <HeartIcon className="h-5 w-5 text-gray-400 hover:text-red-500" />
                        )}
                      </button>
                      {supplier.has_customer_note && (
                        <DocumentTextIcon className="h-4 w-4 text-blue-500" title="Has customer note" />
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    {/* Creator indicator */}
                    <div className="flex items-center text-xs px-2 py-1 rounded-full w-fit">
                      {supplier.created_by === customer?.id ? (
                        <span className="font-medium text-blue-600 bg-blue-50">✓ Created by you</span>
                      ) : (
                        <span className="font-medium text-gray-600 bg-gray-100">Community</span>
                      )}
                    </div>
                    
                    {supplier.contact_email && (
                      <div className="flex items-center text-sm text-gray-600">
                        <EnvelopeIcon className="h-4 w-4 mr-2 text-gray-400" />
                        <a
                          href={`mailto:${supplier.contact_email}`}
                          className="hover:text-primary-600"
                        >
                          {supplier.customer_contact_email}
                        </a>
                      </div>
                    )}
                    
                    {supplier.contact_phone && (
                      <div className="flex items-center text-sm text-gray-600">
                        <PhoneIcon className="h-4 w-4 mr-2 text-gray-400" />
                        <a
                          href={`tel:${supplier.contact_phone}`}
                          className="hover:text-primary-600"
                        >
                          {supplier.contact_phone}
                        </a>
                      </div>
                    )}
                    
                    {supplier.address && (
                      <div className="flex items-start text-sm text-gray-600">
                        <MapPinIcon className="h-4 w-4 mr-2 text-gray-400 mt-0.5" />
                        <span className="line-clamp-2">{supplier.address}</span>
                      </div>
                    )}
                    
                  
                  </div>

                </div>
              </div>
              );
            })}
          </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center">
              <nav className="flex items-center space-x-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                
                {/* Smart pagination - only show relevant pages */}
                {(() => {
                  const pages: React.ReactNode[] = [];
                  const maxVisiblePages = 7; // Show max 7 page buttons
                  const halfVisible = Math.floor(maxVisiblePages / 2);
                  
                  let startPage = Math.max(1, currentPage - halfVisible);
                  let endPage = Math.min(totalPages, currentPage + halfVisible);
                  
                  // Adjust if we're near the beginning or end
                  if (currentPage <= halfVisible) {
                    endPage = Math.min(totalPages, maxVisiblePages);
                  }
                  if (currentPage > totalPages - halfVisible) {
                    startPage = Math.max(1, totalPages - maxVisiblePages + 1);
                  }
                  
                  // Always show first page if not visible
                  if (startPage > 1) {
                    pages.push(
                  <button
                        key={1}
                        onClick={() => setCurrentPage(1)}
                        className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                      >
                        1
                      </button>
                    );
                    if (startPage > 2) {
                      pages.push(
                        <span key="ellipsis1" className="px-3 py-2 text-sm text-gray-500">
                          ...
                        </span>
                      );
                    }
                  }
                  
                  // Show visible pages
                  for (let i = startPage; i <= endPage; i++) {
                    pages.push(
                      <button
                        key={i}
                        onClick={() => setCurrentPage(i)}
                    className={`px-3 py-2 text-sm font-medium rounded-md ${
                          i === currentPage
                        ? 'bg-primary-600 text-white'
                        : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                        {i}
                  </button>
                    );
                  }
                  
                  // Always show last page if not visible
                  if (endPage < totalPages) {
                    if (endPage < totalPages - 1) {
                      pages.push(
                        <span key="ellipsis2" className="px-3 py-2 text-sm text-gray-500">
                          ...
                        </span>
                      );
                    }
                    pages.push(
                      <button
                        key={totalPages}
                        onClick={() => setCurrentPage(totalPages)}
                        className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                      >
                        {totalPages}
                      </button>
                    );
                  }
                  
                  return pages;
                })()}
                
                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </nav>
            </div>
          )}
        </>
      )}

      {/* Add/Edit Supplier Modal - Part 1 */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-6xl mx-4 max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-gray-900">
                {editingSupplier 
                  ? (editingSupplier.created_by === customer?.id 
                      ? 'Edit Supplier' 
                      : 'Edit Supplier Details')
                  : 'Add New Supplier'}
              </h2>
              <button
                onClick={() => setShowAddModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            
            {/* Two Column Layout */}
            <div className="flex gap-6 flex-1 overflow-hidden">
              {/* Left Column - Form */}
              <div className="w-1/2 overflow-y-auto">
            <form onSubmit={handleSubmitSupplier} className="space-y-6">
              {/* Validation Errors */}
              {validationErrors.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <ul className="text-sm text-red-600 space-y-1">
                    {validationErrors.map((error, index) => (
                      <li key={index}>• {error}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Basic Supplier Information */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">Basic Information</h3>
                  {editingSupplier && editingSupplier.created_by !== customer?.id && (
                    <span className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded">
                      Read-only (created by another customer)
                    </span>
                  )}
                </div>
                
                <div>
                  <label className="label">
                    Supplier Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full input"
                    maxLength={255}
                    required
                    disabled={!!(editingSupplier && editingSupplier.created_by !== customer?.id)}
                  />
                </div>


                <div>
                  <label className="label">
                    Office Phone
                  </label>
                  <input
                    type="tel"
                    value={formData.contact_phone}
                    onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                    className="w-full input"
                    maxLength={50}
                    placeholder="+1 (555) 123-4567"
                    disabled={!!(editingSupplier && editingSupplier.created_by !== customer?.id)}
                  />
                </div>

                <div>
                  <label className="label">
                    Address
                  </label>
                  <textarea
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    className="w-full textarea"
                    rows={3}
                    placeholder="Enter the main address..."
                    disabled={!!(editingSupplier && editingSupplier.created_by !== customer?.id)}
                  />
                </div>
              </div>

              {/* Customer-Specific Communication Details */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium text-gray-900">Your Communication Details</h3>
                  <span className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded">
                    Personal to you
                  </span>
                </div>
                
                <div className="flex gap-4">
                  <div className="flex-1">
                    <label className="label">
                      Your Contact Name
                    </label>
                    <input
                      type="text"
                      value={customerDetailsData.contact_name}
                      onChange={(e) => setCustomerDetailsData({ ...customerDetailsData, contact_name: e.target.value })}
                      className="w-full input"
                      maxLength={255}
                      placeholder="Enter your contact person's name"
                    />
                  </div>

                  <div className="flex-1">
                    <label className="label">
                      Your Contact Email
                    </label>
                    <input
                      type="email"
                      value={customerDetailsData.contact_email}
                      onChange={(e) => setCustomerDetailsData({ ...customerDetailsData, contact_email: e.target.value })}
                      className="w-full input"
                      maxLength={255}
                      placeholder="your.contact@company.com"
                    />
                  </div>

                  <div className="flex-1">
                    <label className="label">
                      Your Contact Phone
                    </label>
                    <input
                      type="tel"
                      value={customerDetailsData.phone}
                      onChange={(e) => setCustomerDetailsData({ ...customerDetailsData, phone: e.target.value })}
                      className="w-full input"
                      maxLength={20}
                      placeholder="+44 1234 567890"
                    />
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-1">
                    <label className="label">
                      Preferred Order Method
                    </label>
                    <select
                      value={customerDetailsData.preferred_order_method}
                      onChange={(e) => setCustomerDetailsData({ ...customerDetailsData, preferred_order_method: e.target.value as 'phone' | 'email' | 'purchase_order' | 'zahara' | '' })}
                      className="w-full select"
                    >
                      <option value="">Select preferred method</option>
                      <option value="phone">Phone</option>
                      <option value="email">Email</option>
                      <option value="purchase_order">Purchase Order</option>
                      <option value="zahara">Zahara</option>
                    </select>
                  </div>

                  <div className="flex-1">
                    <label className="label">
                      How do you check availability?
                    </label>
                    <select
                      value={customerDetailsData.availability_check_method}
                      onChange={(e) => setCustomerDetailsData({ ...customerDetailsData, availability_check_method: e.target.value as 'phone' | 'email' | 'website' | '' })}
                      className="w-full select"
                    >
                      <option value="">Select method</option>
                      <option value="phone">Phone</option>
                      <option value="email">Email</option>
                      <option value="website">Website</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Export to Zahara Section */}
              {editingSupplier && zaharaConfigured && !hasZaharaMatch(editingSupplier.id) && (
                <div className="border-t border-gray-200 pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-medium text-gray-900">Zahara Integration</h4>
                      <p className="text-sm text-gray-500 mt-1">
                        Export this supplier to Zahara for seamless integration
                      </p>
                    </div>
                  
                  </div>
                </div>
              )}

              <div className="flex justify-start space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={() => editingSupplier && exportToZahara(editingSupplier.id)}
                      className="btn btn-secondary"
                    >
                      <img
                        src="/images/zahara-logo.svg"
                        alt="Zahara"
                        className="h-4 w-4"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                      <span>Export to Zahara</span>
                    </button>
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="btn btn-soft"
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="btn btn-primary"
                >
                  {submitting 
                    ? 'Saving...' 
                    : (editingSupplier 
                        ? (editingSupplier.created_by === customer?.id ? 'Update Supplier' : 'Save Customer Details')
                        : 'Save')
                  }
                </button>
              </div>
            </form>
              </div>
              
              {/* Right Column - Timeline */}
              {editingSupplier && (
                <div className="w-1/2 border-l border-gray-200 pl-6 flex flex-col">
                  <div className="mb-4">
                    <h3 className="text-lg font-medium text-gray-900">Timeline</h3>
                  </div>
                  
                  <div className="flex-1 overflow-y-auto mb-4">
                    {/* Timeline with connecting lines */}
                    <div className="relative">
                      {/* Timeline line */}
                      <div className="absolute left-2 top-0 bottom-0 w-px bg-gray-200"></div>
                      
                      {/* Dynamic Timeline Events */}
                      {timelineEvents.map((event, index) => (
                        <div key={event.id} className="relative flex items-start space-x-3 pb-4">
                          <div className="flex-shrink-0 relative z-10">
                            <div className={`w-4 h-4 rounded-full border-2 border-white shadow-sm ${
                              event.type === 'note' ? 'bg-blue-500' : 'bg-gray-400'
                            }`}></div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-gray-900 font-medium">{event.title}</p>
                            <p className="text-xs text-gray-500">
                              {event.author} - {new Date(event.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                            </p>
                          </div>
                        </div>
                      ))}
                      
                      {/* Empty state */}
                      {timelineEvents.length === 0 && (
                        <div className="text-center py-8 text-gray-500">
                          <p className="text-sm">No timeline events yet</p>
                          <p className="text-xs">Add a note to get started</p>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Add Note Field at Bottom */}
                  <div className="border-t border-gray-200 pt-4">
                    <div className="space-y-3">
                      <label className="block text-sm font-medium text-gray-700">
                        Add Note
                      </label>
                      <textarea
                        value={customerNote}
                        onChange={(e) => setCustomerNote(e.target.value)}
                        className="w-full textarea textarea-sm"
                        rows={3}
                        placeholder="Add a note about this supplier..."
                      />
                      <button
                        type="button"
                        onClick={handleAddNote}
                        className="btn btn-primary btn-sm w-full"
                        disabled={!customerNote.trim()}
                      >
                        Add Note
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Suppliers;